import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { careersApi } from '@/careers/lib/careersApi';
import { useLoading } from '@/provider/LoadingProvider';
import { ArrowRight, KeyRound } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui';
import { careersErrorMessage } from '@/careers/lib/careersErrorMessage';

interface ForgotPasswordProps {
  isOpen: boolean;
  onClose: () => void;
  // Aday (Peoply Kariyer) hesabı: bağlantı aday hesabına gönderilir.
  candidate?: boolean;
}
const ForgotPasswordModal = ({ isOpen, onClose, candidate = false }: ForgotPasswordProps) => {
  const [email, setEmail] = useState('');
  const queryClient = useQueryClient();
  const { show, hide } = useLoading();
  const { t } = useTranslation();
  useEffect(() => {
    if (isOpen) setEmail('');
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    show();
    try {
      await forgotPassword.mutateAsync(email);
      setEmail('');
      toast.success(t('auth.resetPassword.successMessage', { email }));
      onClose();
    } catch (error) {
      // HR isteğinde hata bildirimi api katmanından gelir; aday isteği kendi istemcisini kullanır.
      if (candidate) toast.error(careersErrorMessage(error));
    } finally {
      hide();
    }
  };
  const forgotPassword = useMutation({
    mutationFn: async (payload: string) => {
      if (candidate) return careersApi.forgotPassword(payload);
      return api.post(`/auth/forgot-password`, { companyEmail: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/auth/forgot-password'] });
    },
  });
  if (!isOpen) {
    return null;
  }
  // Giriş tasarımıyla aynı görsel dil: sıcak beyaz yüzey, lacivert birincil buton, Geist yazı tipi.
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='w-[calc(100%-32px)] max-w-[460px] gap-0 rounded-[22px] border-[var(--public-line-faint)] bg-public-paper p-7 font-[Geist,ui-sans-serif,system-ui,sans-serif] shadow-[0_40px_80px_-40px_rgba(14,20,36,.55)] sm:rounded-[22px] sm:p-9'>
        <DialogHeader className='space-y-0 text-left sm:text-left'>
          <span className='flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-public-accent-soft'>
            <KeyRound className='h-6 w-6 text-public-accent' aria-hidden='true' />
          </span>
          <DialogTitle className='mt-5 text-[26px] font-semibold leading-[1.1] tracking-[-.03em] text-public-ink'>
            {t('auth.resetPassword.title')}
          </DialogTitle>
          <DialogDescription className='mt-2.5 text-[15px] leading-normal text-public-muted'>
            {t('auth.resetPassword.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='mt-7'>
          <Label htmlFor='reset-email' className='mb-2 block text-[13.5px] font-semibold text-public-ink'>
            {t('auth.resetPassword.emailLabel')}
          </Label>
          <Input
            type='email'
            id='reset-email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.resetPassword.emailPlaceholder')}
            required
            className='h-[50px] rounded-[12px] border-[var(--public-line-mid)] bg-white px-4 text-[15px]'
          />

          <div className='mt-7 grid gap-3'>
            <Button
              type='submit'
              disabled={forgotPassword.isPending}
              className='h-[54px] w-full justify-between rounded-[12px] bg-public-ink px-[22px] text-[15.5px] font-semibold text-white shadow-[0_18px_30px_-18px_rgba(14,20,36,.6)] hover:bg-public-ink-hover active:bg-public-ink disabled:bg-public-ink disabled:opacity-60'
            >
              {t('auth.resetPassword.sendResetLink')}
              <ArrowRight className='h-[19px] w-[19px]' aria-hidden='true' />
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              className='h-[50px] w-full rounded-[12px] border-[var(--public-line-mid)] bg-white text-[15px] font-semibold text-public-ink hover:bg-public-warm hover:text-public-ink'
            >
              {t('auth.resetPassword.cancel')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
export default ForgotPasswordModal;
